// ** React Imports
import React, { useEffect, useLayoutEffect, useState } from 'react'

// ** 3rd Party Libraries
import { useDebouncedCallback } from 'use-debounce'

// ** MUI Imports
import { Grid, Typography, Card, CardContent, Button, TextField, Stack, IconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import PageHeader from 'core/components/page-header'
import CircularProgress from '@mui/material/CircularProgress'
import Snackbar from '@mui/material/Snackbar'
import Grow from '@mui/material/Grow'
import InputAdornment from '@mui/material/InputAdornment'

// ** Icons Imports
import DeleteIcon from 'mdi-material-ui/Delete'
import PlusIcon from 'mdi-material-ui/Plus'

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import CustomInputs from '../components/CustomInputs'
import {
  pricePlanSelector,
  createPricePlan,
  deletePricePlan,
  editPricePlan,
  fetchPricePlanData,
  cleanMessages
} from 'store/metaData/pricePlan'

// Styled Card component
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  border: `solid 1px ${theme.palette.grey[300]}`,
  paddingLeft: 3,
  paddingRight: 3
}))

// Styled Button Component
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'uppercase',
  marginLeft: 2,
  marginRight: '1rem',
  fontWeight: 600,
  boxShadow: theme.shadows[4],
  color: theme.palette.common.blue,
  backgroundColor: theme.palette.common.white,
  '&:disabled': {
    color: theme.palette.grey[700],
    boxShadow: 'none'
  },
  '&:hover': {
    backgroundColor: theme.palette.common.white
  }
}))

const PricePlan = () => {
  // ** Redux dispatcher
  const dispatch = useDispatch()

  // ** States
  const [edit, setEdit] = useState(null)
  const [draft, setDraft] = useState(null)
  const [open, setOpen] = useState(false)
  const [data, setData] = useState([])

  // ** Selectors
  const { pricePlans, success, error, loading } = useSelector(pricePlanSelector)

  // ** Fetch data
  useEffect(() => {
    dispatch(fetchPricePlanData())
  }, [])

  useEffect(() => {
    if (pricePlans?.length > 0) setData(pricePlans)
  }, [pricePlans])

  useLayoutEffect(() => {
    if (success == 'CREATE' || success == 'UPDATE') {
      setDraft(null)
      setEdit(null)
      setOpen(true)
    }
    setTimeout(() => {
      dispatch(cleanMessages())
    }, 1500)
  }, [success, error])

  // ** Functions
  const addDraftHandler = () => {
    setDraft({ name: '', value: '' })
    setEdit(null)
  }
  const deleteDraftHandler = () => {
    setDraft(null)
  }

  const addPricePlanHandler = () => {
    if (edit) {
      dispatch(editPricePlan({ id: edit, ...draft }))
    } else {
      dispatch(createPricePlan({ ...draft, name: 'pricePlan-' + draft.value }))
    }
  }

  const deletePricePlanHandler = id => {
    dispatch(deletePricePlan(id))
  }

  const handleCloseMessage = () => {
    setOpen(false)
  }

  const debounced = useDebouncedCallback(
    event => {
      const { name, value } = event.target
      setDraft(prevState => {
        return { ...prevState, [name]: value }
      })
    },
    500,
    // The maximum time func is allowed to be delayed before it's invoked:
    { maxWait: 3000 }
  )

  const discardChanges = () => {
    setEdit(null)
    setDraft(null)
  }

  const onChangeHandler = (event, id) => {
    const { name, value } = event.target
    setData(state => {
      const initialObject = pricePlans.find(el => el.id === id)
      const object = state.find(el => el.id === id)
      if (initialObject.value !== value && !!value) {
        setDraft({ name: object.name, value: value || 0 })
        setEdit(id)
      } else {
        setDraft(null)
        setEdit(null)
      }
      return state.map(item => (item.id === id ? { ...item, value: value ?? 0 } : item))
    })
  }

  return (
    <Grid container spacing={6}>
      <PageHeader
        title={
          <Typography variant='h5' sx={{ color: 'common.blue', fontWeight: 600 }}>
            Price Plans List
          </Typography>
        }
      />
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            {loading == 'GET' && (
              <Grid xs={12} sx={{ ml: '1rem', mb: 5 }}>
                <CircularProgress size='3rem' />
              </Grid>
            )}
            {loading != 'GET' &&
              data?.map((commitment, index) => (
                <Grid key={commitment.id} item xs={12} sx={{ display: 'flex', mb: 5 }}>
                  <Stack direction='row' alignItems='center' gap={1} sx={{ my: 2 }}>
                    <TextField
                      sx={{ mr: 5 }}
                      label='Price Plan'
                      size='small'
                      value={commitment.value}
                      name='value'
                      onChange={event => onChangeHandler(event, commitment.id)}
                      error={error}
                      helperText={error && 'Enter a valid plan'}
                    />
                    <IconButton onClick={() => deletePricePlanHandler(commitment.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Grid>
              ))}
            {draft && !edit ? (
              <Grid item xs={12} sx={{ display: 'flex', mb: 5 }}>
                <Stack direction='row' alignItems='center' gap={3} sx={{ my: 2 }}>
                  <TextField
                    sx={{ mr: 5 }}
                    label='Price plan'
                    size='small'
                    name='value'
                    onChange={debounced}
                  />
                  <IconButton onClick={deleteDraftHandler}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Grid>
            ) : null}
            <Grid xs={12} sx={{ mb: 5 }}>
              <StyledButton variant='text' disabled={draft || edit} onClick={addDraftHandler}>
                <PlusIcon sx={{ mr: 2 }} />
                Add price plan
              </StyledButton>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex' }}>
              <StyledButton
                variant='contained'
                disabled={!draft?.value}
                onClick={addPricePlanHandler}
                startIcon={(loading == 'CREATE' || loading == 'UPDATE') && <CircularProgress size='1rem' />}
              >
                save
              </StyledButton>
              <StyledButton variant='contained' disabled={!(edit || draft)} onClick={discardChanges}>
                discard changes
              </StyledButton>
            </Grid>
          </CardContent>
        </StyledCard>
      </Grid>
      <Snackbar
        sx={{ mt: '3rem' }}
        open={open}
        onClose={handleCloseMessage}
        message='Action done successfully !'
        autoHideDuration={2000}
        key={'top' + 'right'}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      />
    </Grid>
  )
}

export default PricePlan
